import { useTranslate } from '@tolgee/react';
import { message, Tooltip, Upload } from 'antd';
import { ToolbarIcon } from '../ToolbarIcon';
import { useChatConversationContext } from 'views/Conversation/Chat/contexts/ChatConversationContext';
import useSendFileURL from 'hooks/business/useSendFileByURL';
import { useUploadResource } from 'hooks/business/useUploadResource';
import useIM from 'hooks/business/im/useIM';
import { useMemoizedFn } from 'ahooks';
import { get, isString, set } from 'lodash-es';
import { logger } from 'services/logger';
import { uploadClass } from './styles';

function FileUpload({ onSend }: { onSend?: () => void }) {
  const { t } = useTranslate();

  const sendFile = useSendFile();

  return (
    <Tooltip title={t('ViewShops.上传文件')}>
      <Upload
        className={uploadClass}
        name="file"
        showUploadList={false}
        beforeUpload={(file) => {
          if (file.size > 25 * 1024 * 1024) {
            message.warn(t('上传文件大小限制', { size: '25M' }));

            return Upload.LIST_IGNORE;
          }

          sendFile(file, onSend);

          return Upload.LIST_IGNORE;
        }}
      >
        <ToolbarIcon icon="attachment" />
      </Upload>
    </Tooltip>
  );
}

export function useSendFile() {
  const { currentConversation: conversation } = useChatConversationContext();

  const uploadFileURL = useSendFileURL();
  const uploadResource = useUploadResource();

  const { sendFile } = useIM();

  const sendFn = useMemoizedFn(
    async (file: File | string, onSend?: () => void, filename?: string, msgId?: string) => {
      if (!conversation) {
        return;
      }

      const { conversationId, storeId, region } = conversation;

      let uploadPromise;

      const errorCatchHandler = (err: any) => {
        const data = err instanceof Error ? err.toString() : err;

        logger.info(
          `[ResourceUpload]: ${JSON.stringify({
            type: 'uploadFailed',
            data,
          })}`,
        );

        const failCause = get(err, 'failCause');
        if (['upload_failed', 'upload_timeout'].includes(failCause?.code)) {
          set(err, 'failCause', {
            ...failCause,
            reSendImage(id: string) {
              sendFn(file, onSend, filename, id);
            },
          });
        }
        return Promise.reject(err);
      };

      if (isString(file)) {
        uploadPromise = uploadFileURL({
          storeId,
          resourceType: 'IMAGE',
          url: file,
        })
          .then(({ localUrl, url, externalId }) => {
            return {
              id: externalId,
              fileUrl: localUrl || url,
              name: file.split('/').pop() || '',
            };
          })
          .catch(errorCatchHandler);
      } else {
        uploadPromise = uploadResource({
          file,
          storeId,
          region,
          fileType: 'IMAGE',
          reqSource: 'CONVERSATION',
        })
          .then(({ url, localUrl, externalId }) => {
            return {
              id: externalId,
              mimeType: file.type,
              fileUrl: localUrl || url,
              size: file.size,
              name: file.name,
            };
          })
          .catch(errorCatchHandler);
      }

      sendFile(
        conversationId,
        uploadPromise,
        isString(file)
          ? {
              name: file.split('/').pop() || '',
            }
          : {
              name: file.name,
              size: file.size,
            },
        msgId?.replace('nim-', ''),
      ).finally(() => {
        onSend && onSend();
      });
    },
  );

  return sendFn;
}

export default FileUpload;
